import { Check, ChevronDown, ChevronRight, ChevronUp, Minimize2, TriangleAlert, X } from "lucide-react";
import { CSSProperties, useMemo } from "react";
import { Link, useNavigate } from "react-router";

import { StatusPill, type StatusPillTone } from "nazare-ui/components";
import { useSortableRows } from "nazare-ui/utils";

import { runViewTransition } from "../../../../run-view-transition";
import { useDocumentTitle } from "../../../../use-document-title";
import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../../scroll-shadow";
import { InfiniteScrollSentinel, SkeletonRow } from "../../../../skeleton-row";
import { useInfiniteRows } from "../../../../use-infinite-rows";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useSimulatedLoad } from "../../../../use-simulated-load";
import styles from "./recent-runs.module.css";

/**
 * Full-page `/qa-automation/recent-runs` route: the "maximised" table view
 * of the QA automation dashboard's `RecentRunsTable` card
 * (`../recent-runs.tsx`), opened via a grow transition from that card
 * (shared `viewTransitionName: "qa-card-recent-runs"`). That dashboard file
 * is owned/edited elsewhere concurrently, so this page deliberately does NOT
 * import from it — the `RunRow` type, hand-authored `RECENT_RUNS` sample, and
 * the hashSeed/mulberry32/formatAge/generateSyntheticRun/getRecentRun/
 * STATUS_META/EnvPill/CountsBreakdown helpers below are verbatim copies,
 * extended to a fuller "maximised" history (400 total rows instead of the
 * dashboard card's abbreviated 140) for infinite scroll on this page. All
 * data is static mock content — there is no live QA-automation backend in
 * `platform` yet.
 */

type RunEnv = "dev" | "qa" | "prod" | "local" | "other";
type RunStatus = "passed" | "warned" | "failed";

type RunRow = {
  id: string;
  when: string;
  /** Minutes elapsed since the run — the real, comparable value the "When"
   * column sorts by (`when` itself is a display string like "yesterday",
   * not something meaningfully sortable as text). */
  ageMinutes: number;
  env: RunEnv;
  user: string;
  project: string;
  status: RunStatus;
  counts: { pass: number; warn: number; fail: number };
  duration: string;
  cost: string;
  tokensIn: string;
  tokensOut: string;
};

const RECENT_RUNS: RunRow[] = [
  {
    id: "run-1",
    when: "about 10 hours ago",
    ageMinutes: 600,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "desktop",
    status: "warned",
    counts: { pass: 36, warn: 12, fail: 0 },
    duration: "1h 12m 5s",
    cost: "~$4.82 est.",
    tokensIn: "506.5k",
    tokensOut: "14.8k",
  },
  {
    id: "run-2",
    when: "about 14 hours ago",
    ageMinutes: 840,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "mobile",
    status: "passed",
    counts: { pass: 52, warn: 0, fail: 0 },
    duration: "48m 21s",
    cost: "~$3.10 est.",
    tokensIn: "402.1k",
    tokensOut: "9.6k",
  },
  {
    id: "run-3",
    when: "yesterday",
    ageMinutes: 1440,
    env: "dev",
    user: "thomas@phoenixcourt.vc",
    project: "web",
    status: "failed",
    counts: { pass: 28, warn: 4, fail: 3 },
    duration: "36m 58s",
    cost: "~$2.44 est.",
    tokensIn: "298.7k",
    tokensOut: "7.2k",
  },
  {
    id: "run-4",
    when: "2 days ago",
    ageMinutes: 2880,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "admin-console",
    status: "passed",
    counts: { pass: 41, warn: 0, fail: 0 },
    duration: "58m 3s",
    cost: "~$3.67 est.",
    tokensIn: "455.2k",
    tokensOut: "11.1k",
  },
  {
    id: "run-5",
    when: "3 days ago",
    ageMinutes: 4320,
    env: "local",
    user: "priya@phoenixcourt.vc",
    project: "desktop",
    status: "warned",
    counts: { pass: 33, warn: 6, fail: 0 },
    duration: "44m 12s",
    cost: "~$2.91 est.",
    tokensIn: "331.4k",
    tokensOut: "8.4k",
  },
  {
    id: "run-6",
    when: "4 days ago",
    ageMinutes: 5760,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "billing",
    status: "passed",
    counts: { pass: 19, warn: 0, fail: 0 },
    duration: "22m 40s",
    cost: "~$1.38 est.",
    tokensIn: "168.9k",
    tokensOut: "4.1k",
  },
  {
    id: "run-7",
    when: "5 days ago",
    ageMinutes: 7200,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "mobile",
    status: "failed",
    counts: { pass: 47, warn: 2, fail: 5 },
    duration: "1h 4m 51s",
    cost: "~$5.29 est.",
    tokensIn: "544.0k",
    tokensOut: "13.9k",
  },
  {
    id: "run-8",
    when: "6 days ago",
    ageMinutes: 8640,
    env: "other",
    user: "marcus@phoenixcourt.vc",
    project: "onboarding",
    status: "passed",
    counts: { pass: 15, warn: 0, fail: 0 },
    duration: "12m 9s",
    cost: "~$0.87 est.",
    tokensIn: "97.3k",
    tokensOut: "2.6k",
  },
  {
    id: "run-9",
    when: "7 days ago",
    ageMinutes: 10080,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "desktop",
    status: "passed",
    counts: { pass: 22, warn: 0, fail: 0 },
    duration: "19m 47s",
    cost: "~$1.21 est.",
    tokensIn: "142.6k",
    tokensOut: "3.7k",
  },
  {
    id: "run-10",
    when: "8 days ago",
    ageMinutes: 11520,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "billing",
    status: "warned",
    counts: { pass: 30, warn: 3, fail: 0 },
    duration: "33m 15s",
    cost: "~$2.05 est.",
    tokensIn: "221.9k",
    tokensOut: "5.9k",
  },
  {
    id: "run-11",
    when: "9 days ago",
    ageMinutes: 12960,
    env: "dev",
    user: "priya@phoenixcourt.vc",
    project: "web",
    status: "passed",
    counts: { pass: 18, warn: 0, fail: 0 },
    duration: "15m 32s",
    cost: "~$0.98 est.",
    tokensIn: "108.4k",
    tokensOut: "2.9k",
  },
  {
    id: "run-12",
    when: "9 days ago",
    ageMinutes: 13200,
    env: "local",
    user: "thomas@phoenixcourt.vc",
    project: "mobile",
    status: "failed",
    counts: { pass: 24, warn: 1, fail: 2 },
    duration: "28m 06s",
    cost: "~$1.87 est.",
    tokensIn: "196.3k",
    tokensOut: "4.8k",
  },
  {
    id: "run-13",
    when: "10 days ago",
    ageMinutes: 14400,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "admin-console",
    status: "passed",
    counts: { pass: 45, warn: 0, fail: 0 },
    duration: "51m 40s",
    cost: "~$3.32 est.",
    tokensIn: "387.5k",
    tokensOut: "10.2k",
  },
  {
    id: "run-14",
    when: "11 days ago",
    ageMinutes: 15840,
    env: "other",
    user: "marcus@phoenixcourt.vc",
    project: "onboarding",
    status: "warned",
    counts: { pass: 20, warn: 5, fail: 0 },
    duration: "25m 18s",
    cost: "~$1.55 est.",
    tokensIn: "173.8k",
    tokensOut: "4.4k",
  },
  {
    id: "run-15",
    when: "12 days ago",
    ageMinutes: 17280,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "desktop",
    status: "passed",
    counts: { pass: 38, warn: 0, fail: 0 },
    duration: "42m 55s",
    cost: "~$2.78 est.",
    tokensIn: "312.1k",
    tokensOut: "8.1k",
  },
  {
    id: "run-16",
    when: "13 days ago",
    ageMinutes: 18720,
    env: "dev",
    user: "thomas@phoenixcourt.vc",
    project: "web",
    status: "failed",
    counts: { pass: 12, warn: 2, fail: 6 },
    duration: "18m 22s",
    cost: "~$1.14 est.",
    tokensIn: "121.7k",
    tokensOut: "3.2k",
  },
  {
    id: "run-17",
    when: "14 days ago",
    ageMinutes: 20160,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "billing",
    status: "passed",
    counts: { pass: 27, warn: 0, fail: 0 },
    duration: "31m 09s",
    cost: "~$1.94 est.",
    tokensIn: "204.6k",
    tokensOut: "5.3k",
  },
  {
    id: "run-18",
    when: "15 days ago",
    ageMinutes: 21600,
    env: "local",
    user: "priya@phoenixcourt.vc",
    project: "mobile",
    status: "warned",
    counts: { pass: 16, warn: 4, fail: 0 },
    duration: "14m 51s",
    cost: "~$0.91 est.",
    tokensIn: "97.8k",
    tokensOut: "2.5k",
  },
  {
    id: "run-19",
    when: "16 days ago",
    ageMinutes: 23040,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "admin-console",
    status: "passed",
    counts: { pass: 60, warn: 0, fail: 0 },
    duration: "1h 8m 47s",
    cost: "~$4.42 est.",
    tokensIn: "468.9k",
    tokensOut: "12.6k",
  },
  {
    id: "run-20",
    when: "17 days ago",
    ageMinutes: 24480,
    env: "other",
    user: "marcus@phoenixcourt.vc",
    project: "desktop",
    status: "passed",
    counts: { pass: 21, warn: 0, fail: 0 },
    duration: "20m 14s",
    cost: "~$1.28 est.",
    tokensIn: "138.2k",
    tokensOut: "3.5k",
  },
  {
    id: "run-21",
    when: "18 days ago",
    ageMinutes: 25920,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "web",
    status: "failed",
    counts: { pass: 9, warn: 1, fail: 4 },
    duration: "11m 38s",
    cost: "~$0.72 est.",
    tokensIn: "76.4k",
    tokensOut: "2.0k",
  },
  {
    id: "run-22",
    when: "19 days ago",
    ageMinutes: 27360,
    env: "dev",
    user: "thomas@phoenixcourt.vc",
    project: "billing",
    status: "passed",
    counts: { pass: 33, warn: 0, fail: 0 },
    duration: "37m 26s",
    cost: "~$2.39 est.",
    tokensIn: "289.3k",
    tokensOut: "7.5k",
  },
  {
    id: "run-23",
    when: "20 days ago",
    ageMinutes: 28800,
    env: "local",
    user: "priya@phoenixcourt.vc",
    project: "mobile",
    status: "warned",
    counts: { pass: 25, warn: 3, fail: 0 },
    duration: "26m 03s",
    cost: "~$1.63 est.",
    tokensIn: "181.5k",
    tokensOut: "4.6k",
  },
  {
    id: "run-24",
    when: "21 days ago",
    ageMinutes: 30240,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "onboarding",
    status: "passed",
    counts: { pass: 17, warn: 0, fail: 0 },
    duration: "16m 40s",
    cost: "~$1.02 est.",
    tokensIn: "112.9k",
    tokensOut: "2.8k",
  },
];

const STATUS_META: Record<RunStatus, { label: string; tone: StatusPillTone }> = {
  passed: { label: "Passed", tone: "success" },
  warned: { label: "Warned", tone: "warning" },
  failed: { label: "Failed", tone: "error" },
};

/**
 * The "maximised" full-page history goes deeper than the dashboard card's
 * own abbreviated `TOTAL_RECENT_RUN_COUNT` (140) — a busy QA pipeline
 * realistically has hundreds of past runs, and a dedicated history page is
 * exactly where that fuller depth belongs. Same FNV-1a hash + mulberry32
 * PRNG technique as `../../workflows/pages/runs.tsx`'s `generateSyntheticRun`
 * (and the dashboard card's own copy of this same technique), so the SAME
 * index always produces the SAME synthetic row — no `Math.random()`/
 * `Date.now()`.
 */
const TOTAL_RECENT_RUN_COUNT = 400;

function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const ENV_POOL: RunEnv[] = ["dev", "qa", "prod", "local", "other"];
const STATUS_POOL: RunStatus[] = ["passed", "passed", "passed", "warned", "failed"];
const USER_POOL = Array.from(new Set(RECENT_RUNS.map((run) => run.user)));
const PROJECT_POOL = Array.from(new Set(RECENT_RUNS.map((run) => run.project)));

/** "X days ago" for an age beyond what `Intl.RelativeTimeFormat`'s hour/day
 * granularity in the dashboard's `formatRelative` would bother distinguishing
 * — every synthetic row here is at least 22 days old (the hand-authored rows
 * already cover 0-21 days), so a flat "N days ago" is both simpler and reads
 * identically to the real thing at that distance. */
function formatAge(ageMinutes: number): string {
  const days = Math.floor(ageMinutes / (24 * 60));
  return `${days} days ago`;
}

const LAST_HARDCODED_AGE_MINUTES = RECENT_RUNS[RECENT_RUNS.length - 1].ageMinutes;

/** A run beyond the hand-authored `RECENT_RUNS` sample — deterministic per
 * `index`. Fixed 1-day step plus jitter smaller than the step, so age stays
 * strictly increasing (never a LATER row showing a MORE RECENT age than an
 * EARLIER row) — same technique as `../../workflows/pages/runs.tsx`'s
 * `generateSyntheticRun`. */
function generateSyntheticRun(index: number): RunRow {
  const rand = mulberry32(hashSeed(`qa-recent-run-page-${index}`));

  const rowsBeyond = index - RECENT_RUNS.length + 1;
  const stepMinutes = 24 * 60;
  const jitterMinutes = Math.floor(rand() * 600); // up to 10h, smaller than the 1-day step
  const ageMinutes = LAST_HARDCODED_AGE_MINUTES + rowsBeyond * stepMinutes + jitterMinutes;

  const status = STATUS_POOL[Math.floor(rand() * STATUS_POOL.length)];
  const pass = 8 + Math.floor(rand() * 50);
  const warn = status === "warned" ? 1 + Math.floor(rand() * 8) : 0;
  const fail = status === "failed" ? 1 + Math.floor(rand() * 6) : 0;
  const durationMinutes = 8 + Math.floor(rand() * 55);
  const durationSeconds = Math.floor(rand() * 60);
  const costDollars = (0.6 + rand() * 4.5).toFixed(2);
  const tokensInK = (60 + rand() * 480).toFixed(1);
  const tokensOutK = (1.5 + rand() * 13).toFixed(1);

  return {
    id: `run-page-synthetic-${index}`,
    when: formatAge(ageMinutes),
    ageMinutes,
    env: ENV_POOL[Math.floor(rand() * ENV_POOL.length)],
    user: USER_POOL[Math.floor(rand() * USER_POOL.length)],
    project: PROJECT_POOL[Math.floor(rand() * PROJECT_POOL.length)],
    status,
    counts: { pass, warn, fail },
    duration: `${durationMinutes}m ${String(durationSeconds).padStart(2, "0")}s`,
    cost: `~$${costDollars} est.`,
    tokensIn: `${tokensInK}k`,
    tokensOut: `${tokensOutK}k`,
  };
}

function getRecentRun(index: number): RunRow {
  return index < RECENT_RUNS.length ? RECENT_RUNS[index] : generateSyntheticRun(index);
}

function EnvPill({ env }: { env: RunEnv }) {
  return <span className={styles.envPill}>{env}</span>;
}

function CountsBreakdown({ counts }: { counts: RunRow["counts"] }) {
  return (
    <span className={styles.counts}>
      <Check size={11} strokeWidth={2.5} aria-hidden />
      {counts.pass}
      <span aria-hidden>·</span>
      <TriangleAlert size={11} strokeWidth={2.5} aria-hidden />
      {counts.warn}
      <span aria-hidden>·</span>
      <X size={11} strokeWidth={2.5} aria-hidden />
      {counts.fail}
    </span>
  );
}

export function QaRecentRunsPage() {
  useDocumentTitle("Recent runs");

  const navigate = useNavigate();
  const goToDashboard = () => runViewTransition(() => navigate("/qa-automation"));

  // Initial page-mount loading state — distinct from `isLoadingMore` below,
  // which only covers the infinite-scroll "load more" case further down the
  // list. This one covers the very first render, before any real rows (not
  // even the first `initialCount` page) have ever appeared.
  const isInitialLoading = useSimulatedLoad();

  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const { loadedCount, isLoadingMore, hasMore, sentinelRef } = useInfiniteRows({
    totalCount: TOTAL_RECENT_RUN_COUNT,
    initialCount: RECENT_RUNS.length,
    pageSize: 30,
    scrollRootRef: tableWrapRef,
  });

  // Sorting applies to whatever's been loaded so far, same load-then-sort
  // convention as every other infinite-scroll table in this app — it
  // doesn't fetch ahead to re-sort rows beyond `loadedCount`.
  const loadedRuns = useMemo(
    () => Array.from({ length: loadedCount }, (_, index) => getRecentRun(index)),
    [loadedCount],
  );

  const { sorted, direction, toggle } = useSortableRows(
    loadedRuns,
    (run) => run.ageMinutes,
    "asc", // ageMinutes ascending = most recent first, matches the dashboard card's default order
  );

  return (
    <div className={styles.page}>
      <section
        className={styles.card}
        style={{ viewTransitionName: "qa-card-recent-runs" } as unknown as CSSProperties}
      >
        <header className={styles.header}>
          <h2 className={styles.title}>
            Recent runs
            <span className={styles.count}>{TOTAL_RECENT_RUN_COUNT} runs</span>
          </h2>
          <button
            type="button"
            className={styles.minimiseButton}
            onClick={goToDashboard}
            aria-label="Minimise to dashboard"
            title="Minimise to dashboard"
          >
            <Minimize2 className={styles.minimiseIcon} aria-hidden="true" />
          </button>
        </header>

        <div className={styles.scrollShadowWrapper}>
          <div ref={tableWrapRef} className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th} scope="col">
                    <button
                      type="button"
                      className={styles.thSort}
                      onClick={toggle}
                      aria-label={`Sort by When, currently ${direction === "asc" ? "newest first" : "oldest first"}`}
                    >
                      When
                      {direction === "asc" ? (
                        <ChevronUp size={12} aria-hidden />
                      ) : (
                        <ChevronDown size={12} aria-hidden />
                      )}
                    </button>
                  </th>
                  <th className={styles.th} scope="col">
                    Env
                  </th>
                  <th className={styles.th} scope="col">
                    User
                  </th>
                  <th className={styles.th} scope="col">
                    Project
                  </th>
                  <th className={styles.th} scope="col">
                    Status
                  </th>
                  <th className={styles.th} scope="col">
                    Duration
                  </th>
                  <th className={styles.th} scope="col">
                    Est. cost
                  </th>
                  <th className={styles.th} scope="col" aria-hidden />
                </tr>
              </thead>
              <tbody>
                {isInitialLoading ? (
                  Array.from({ length: 8 }, (_, index) => (
                    <SkeletonRow key={`initial-skeleton-${index}`} columns={8} />
                  ))
                ) : (
                  <>
                    {sorted.map((run) => (
                      <tr key={run.id} className={styles.row}>
                        <td className={styles.td}>
                          {/* Stretched-link pattern: a `<tr>` can't be wrapped
                             in an `<a>` (invalid HTML), so this empty,
                             visually-hidden `<Link>` — rendered as the first
                             child of the row's first `<td>` — is absolutely
                             positioned to cover the entire row (`.rowLink` =
                             `position: absolute; inset: 0`, `.row` =
                             `position: relative`). A real `<a href>` for the
                             whole row, so cmd/ctrl/middle-click all work,
                             not just an onClick handler. */}
                          <Link
                            to={`/qa-automation/recent-runs#${run.id}`}
                            viewTransition
                            className={styles.rowLink}
                            aria-label={`View run ${run.id}`}
                          />
                          <span className={styles.whenLink}>{run.when}</span>
                        </td>
                        <td className={styles.td}>
                          <EnvPill env={run.env} />
                        </td>
                        <td className={styles.td}>
                          <span className={styles.userCell}>{run.user}</span>
                        </td>
                        <td className={`${styles.td} ${styles.projectCell}`}>
                          {run.project}
                        </td>
                        <td className={styles.td}>
                          <div className={styles.statusCell}>
                            <StatusPill tone={STATUS_META[run.status].tone}>
                              {STATUS_META[run.status].label}
                            </StatusPill>
                            <CountsBreakdown counts={run.counts} />
                          </div>
                        </td>
                        <td className={`${styles.td} ${styles.durationCell}`}>
                          {run.duration}
                        </td>
                        <td className={styles.td}>
                          <div className={styles.costCell}>{run.cost}</div>
                          <div className={styles.tokensCell}>
                            {run.tokensIn} in · {run.tokensOut} out
                          </div>
                        </td>
                        <td className={`${styles.td} ${styles.actionCell}`}>
                          <span className={styles.viewLink}>
                            View
                            <ChevronRight size={13} aria-hidden />
                          </span>
                        </td>
                      </tr>
                    ))}
                    {hasMore && (
                      <InfiniteScrollSentinel sentinelRef={sentinelRef} columns={8} />
                    )}
                    {isLoadingMore &&
                      Array.from({ length: 3 }, (_, index) => (
                        <SkeletonRow key={`skeleton-${index}`} columns={8} />
                      ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
        </div>
      </section>
    </div>
  );
}
